/**
 * Single source of truth for lab partner metadata.
 *
 * Every screen that renders a partner (orders list, order drawer, analytics)
 * used to keep its own copy of this map, which is how "Orange" / "Orange Labs" /
 * "Orange Health" all ended up on screen at once and how the orders filter
 * silently lost its Redcliffe option. Anything provider-specific — labels,
 * tones, the partner-side identifier, which fields that partner actually
 * populates — belongs here, not in a component.
 *
 * Provider ids match the backend LabOrder `provider` enum ("orange_labs" |
 * "redcliffe") and are the exact values the /admin/lab/orders `provider` query
 * param accepts.
 */

const NEUTRAL_TONE = "#64748b";

/**
 * Partner-details fields, per provider.
 *
 * Each entry is data only — the drawer owns the rendering:
 *   key       field on the order document
 *   label     row label
 *   value     optional (order) => value, for composed fields
 *   mono      render monospaced
 *   copyable  show the copy button
 *   type      "date" | "link" | undefined (plain text)
 */
const ORANGE_DETAIL_FIELDS = [
  { key: "ohRequestId", label: "OH request", mono: true, copyable: true },
  { key: "ohToken", label: "OH token", mono: true, copyable: true },
  { key: "ohStatus", label: "OH status" },
  {
    key: "emedicName",
    label: "Emedic",
    value: (o) => (o.emedicName ? [o.emedicName, o.emedicNumber].filter(Boolean).join(" · ") : null),
  },
  { key: "hubName", label: "Hub" },
];

const REDCLIFFE_DETAIL_FIELDS = [
  // Redcliffe books in two phases: rcOrderId exists before payment, so
  // rcConfirmedAt — not rcOrderId — is what says the booking is real.
  { key: "rcOrderId", label: "Redcliffe booking", mono: true, copyable: true },
  { key: "rcStatus", label: "Redcliffe status" },
  { key: "rcSlotId", label: "Slot ID", mono: true },
  { key: "rcTempBookingAt", label: "Temp booking", type: "date" },
  { key: "rcConfirmedAt", label: "Confirmed", type: "date" },
  { key: "trackingLink", label: "Phlebo tracking", type: "link" },
];

export const LAB_PROVIDERS = {
  orange_labs: {
    id: "orange_labs",
    label: "Orange Health",
    tone: "#ea580c",
    idField: "ohRequestId",
    idLabel: "OH request",
    tokenField: "ohToken",
    detailFields: ORANGE_DETAIL_FIELDS,
    reschedule: {
      note: "Calls the Orange Health reschedule API with the stored token.",
      slotHelp: "Must match an available Orange Health serviceability slot.",
      warning: null,
    },
    cancel: {
      // Orange confirms cancellation out-of-band: the route marks the order
      // CancellationRequested and waits for the order.cancelled webhook.
      note: "This action cannot be undone. Orange Health confirms the cancellation asynchronously, so the status will read “CancellationRequested” until their webhook lands.",
      warning: null,
    },
  },
  redcliffe: {
    id: "redcliffe",
    label: "Redcliffe Labs",
    tone: "#db2777",
    idField: "rcOrderId",
    idLabel: "Redcliffe booking",
    // Redcliffe issues no per-order token — it identifies bookings by id alone.
    tokenField: null,
    detailFields: REDCLIFFE_DETAIL_FIELDS,
    reschedule: {
      note: "Calls the Redcliffe reschedule API. The booking is identified by its booking ID — Redcliffe issues no token.",
      slotHelp: "Must match an available Redcliffe collection slot.",
      warning:
        "Redcliffe allows at most 2 reschedules, and none once a phlebotomist has been assigned — it rejects those with “Can’t change booking at this stage”. We don’t track the count, so this may still be refused by Redcliffe.",
    },
    cancel: {
      // Unlike Orange, Redcliffe's center-update-booking answers synchronously
      // with booking_status: "cancelled" — there is no webhook to wait on.
      note: "This action cannot be undone. Redcliffe confirms the cancellation immediately in its response.",
      warning:
        "Redcliffe treats cancellation of a confirmed booking as chargeable, and refuses it entirely once a phlebotomist has been assigned.",
    },
  },
};

/**
 * Metadata for a provider id. NEVER returns undefined: an unknown or missing
 * provider degrades to a neutral, honestly-labelled entry rather than being
 * mislabelled as a real partner.
 *
 * @param {string} [id]
 */
export function getProviderMeta(id) {
  const known = id && LAB_PROVIDERS[id];
  if (known) return known;
  return {
    id: id || "unknown",
    label: id || "—",
    tone: NEUTRAL_TONE,
    idField: null,
    idLabel: "Partner reference",
    tokenField: null,
    detailFields: [],
    reschedule: {
      note: "Applies the new slot to this order. Partner behaviour is unknown for this provider.",
      slotHelp: "Must match a slot the partner has available.",
      warning: null,
    },
    cancel: {
      note: "This action cannot be undone. Partner behaviour is unknown for this provider.",
      warning: null,
    },
    isKnown: false,
  };
}

/** All known providers, in registry order. */
export function listProviders() {
  return Object.values(LAB_PROVIDERS);
}

/** The partner-side booking id for an order, whichever provider placed it. */
export function getPartnerId(order) {
  if (!order) return null;
  const { idField } = getProviderMeta(order.provider);
  return (idField && order[idField]) || null;
}
